import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePreviewExchangeRatesMutation, usePreviewMissingRatePeriodMutation } from "@/api/exchange-rates";
import type { RatePreview, RateRow } from "@/types/exchange-rates";
import { rateErrorMessage } from "../rate-errors";
import type { SpendListParams } from "@/types/spend";

interface FormValues {
  source: string;
  period_label: string;
  valid_from: string;
  valid_through: string;
  exceptional_use: boolean;
  approved: boolean;
  reason: string;
  file: FileList;
}
interface Props {
  source: string;
  sources: string[];
  context: SpendListParams;
  reference?: RateRow;
  onPreview: (preview: RatePreview) => void;
  onCancel: () => void;
}
export const RateImportForm = ({ source, sources, context, reference, onPreview, onCancel }: Props) => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { source: reference?.source ?? source, exceptional_use: false, approved: false, reason: "" },
  });
  const [previewWorkbook, workbookState] = usePreviewExchangeRatesMutation();
  const [previewMissing, missingState] = usePreviewMissingRatePeriodMutation();
  const [error, setError] = React.useState<unknown>();
  const errorRef = React.useRef<HTMLParagraphElement>(null);
  React.useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const submitting = workbookState.isLoading || missingState.isLoading;
  const submit = handleSubmit(async values => {
    setError(undefined);
    const configuration = {
      source: values.source.trim(),
      period_label: values.period_label,
      valid_from: values.valid_from,
      valid_through: values.valid_through,
      exceptional_use: values.exceptional_use,
      reason: values.reason || undefined,
      context,
    };
    try {
      const preview = reference?.id
        ? await previewMissing({ ...configuration, reference_rate_id: reference.id, approved: true }).unwrap()
        : await previewWorkbook({ configuration, file: values.file[0] }).unwrap();
      onPreview(preview);
    } catch (error) {
      setError(error);
    }
  });
  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-lg border bg-white p-5"
      aria-label={reference ? "Approved missing period" : "Exchange rate import"}
    >
      <h2 className="font-semibold">{reference ? "Add approved rate for missing period" : "Upload and configure"}</h2>
      {reference ? (
        <p>
          Reuse 1 {reference.from_currency} = {reference.rate} GBP. The original interval remains unchanged.
        </p>
      ) : (
        <label className="block">
          Finance workbook (.xlsx, maximum 25 MB)
          <Input
            type="file"
            accept=".xlsx"
            {...register("file", {
              validate: files =>
                (!!files?.length &&
                  files[0].name.toLowerCase().endsWith(".xlsx") &&
                  files[0].size > 0 &&
                  files[0].size <= 25_000_000) ||
                "Choose a non-empty XLSX workbook up to 25 MB.",
            })}
          />
        </label>
      )}
      <label className="block">
        Source
        <Input
          list="rate-sources"
          readOnly={!!reference}
          {...register("source", {
            required: "Source is required.",
            maxLength: 125,
            setValueAs: (value: string) => value.trim(),
            validate: value =>
              !Array.from(value).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
              "Source must not contain control characters.",
          })}
        />
        <datalist id="rate-sources">
          {sources.map(value => (
            <option key={value} value={value} />
          ))}
        </datalist>
      </label>
      <label className="block">
        Period label
        <Input {...register("period_label", { required: "Period label is required.", maxLength: 64 })} />
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label>
          Valid from
          <Input type="date" {...register("valid_from", { required: "Start date is required." })} />
        </label>
        <label>
          Valid through (inclusive)
          <Input
            type="date"
            max="9999-12-30"
            {...register("valid_through", {
              required: "End date is required.",
              validate: value => value >= getValues("valid_from") || "End date must be on or after start date.",
            })}
          />
        </label>
      </div>
      <label className="flex items-start gap-2">
        <input type="checkbox" {...register("exceptional_use")} />
        This rate is authorized for use outside its labelled fiscal period.
      </label>
      {reference ? (
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            {...register("approved", { validate: value => value || "Approval for these dates is required." })}
          />
          I confirm this rate is approved for the new validity dates.
        </label>
      ) : null}
      <label className="block">
        Authorization / reason
        <Input
          {...register("reason", {
            maxLength: 2000,
            validate: value =>
              !(reference || getValues("exceptional_use")) ||
              !!value.trim() ||
              "A recorded authorization reason is required.",
          })}
        />
      </label>
      {Object.entries(errors).map(([name, error]) => (
        <p role="alert" key={name} className="text-red-700">
          {error.message ?? `Check ${name}.`}
        </p>
      ))}
      {error ? (
        <p ref={errorRef} tabIndex={-1} role="alert" className="text-red-700">
          {rateErrorMessage(error)}
        </p>
      ) : null}
      <div className="flex gap-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Preparing preview…" : "Preview rates"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
